import { z } from 'zod';
import { AnswerBank } from './answer-bank';
import { CandidateProfile } from './user-profile';
import { Preferences } from './preferences';
import { Resume } from './resume';

/**
 * UserContext is the runtime payload the worker hydrates BEFORE starting a
 * cycle. It bundles everything `runCycleForUser` needs in one shape so the
 * worker doesn't have to talk to the cloud API mid-cycle for config reads.
 *
 * Composition strategy:
 *  - `auth` carries the secrets needed at the LinkedIn / ATS layer; never
 *    leaves the user's device.
 *  - `apiBaseUrl` + `apiJwt` let the worker post progress + LLM calls
 *    back to the OutLinked cloud API.
 *  - `preferences`, `answerBank`, `candidateProfile`, `resume` are the
 *    snapshot of the user's stored config at cycle-start.
 *  - `resumeLocalPath` is the on-device file path of the resume PDF, used
 *    for external ATS file uploads (LinkedIn Easy Apply can use the
 *    already-uploaded resume on LinkedIn's side; external ATS needs a
 *    local file handle).
 */

export const UserContextAuth = z.object({
  linkedinEmail: z.string().email(),
  linkedinPassword: z.string().min(1),
  /**
   * The email the user signed up to OutLinked with (the app-login email
   * held on the mobile side and echoed back via `/v1/users/me`). Optional
   * because older UserContext hydrators (pre-#600 desktop builds, CLI
   * smoke tests) type-compile without source changes and the ATS
   * form-fill fallback chain terminates at linkedinEmail — which IS
   * schema-required — so appLoginEmail acts as a belt-and-suspenders
   * secondary fallback. #600.
   */
  appLoginEmail: z.string().email().optional(),
  // The ATS-credentials.json shape — username/password per ATS hostname.
  atsCredentials: z
    .record(
      z.string(),
      z.object({
        username: z.string(),
        password: z.string(),
      }),
    )
    .default({}),
  /**
   * Per-tenant Workday credentials, keyed by the lowercased tenant
   * hostname (e.g. `lockheed.wd1.myworkdayjobs.com`). Hydrated from the
   * `/v1/workday-tenant-creds` API at cycle-start. When a Workday job's
   * tenant hostname is NOT a key in this map, the worker emits a
   * `needs_attention` event (reason: `workday_credentials_missing`) and
   * skips the job — we don't auto-create accounts on the user's behalf
   * via this path. Default `{}` so older UserContext hydrators (CLI
   * smoke tests, pre-Workday-creds desktop builds) type-compile without
   * source changes.
   */
  workdayTenantCreds: z
    .record(
      z.string(),
      z.object({
        email: z.string().email(),
        password: z.string().min(1),
      }),
    )
    .default({}),
  /**
   * Global Workday credentials — ONE pair per user, used on every Workday
   * tenant the worker encounters. When present, the dispatcher signs in
   * (or creates the account via legacy-ported flow) using these. When
   * absent AND no per-tenant override in `workdayTenantCreds` either,
   * the worker emits `workday_credentials_missing` and skips the job.
   *
   * Hydrated from `Keychain.getAtsAccount()` at cycle-start (#392 —
   * stored in the desktop's macOS Keychain only, never round-trips
   * through the cloud). Optional so older UserContext hydrators (CLI
   * smoke, pre-Workday-auth desktop builds) type-compile without
   * source changes. (#333 → #392)
   */
  atsAccount: z
    .object({
      email: z.string().email(),
      password: z.string().min(1),
    })
    .optional(),
});
export type UserContextAuth = z.infer<typeof UserContextAuth>;

export const UserContextApi = z.object({
  baseUrl: z.string().url(),
  jwt: z.string().min(1),
});
export type UserContextApi = z.infer<typeof UserContextApi>;

export const UserContextPaths = z.object({
  playwrightProfileDir: z.string().min(1),
  resumeLocalPath: z.string().min(1).optional(),
  logDir: z.string().min(1),
});
export type UserContextPaths = z.infer<typeof UserContextPaths>;

export const UserContext = z.object({
  userId: z.string().min(1),
  runId: z.string().min(1),
  api: UserContextApi,
  auth: UserContextAuth,
  paths: UserContextPaths,
  preferences: Preferences,
  answerBank: AnswerBank,
  candidateProfile: CandidateProfile,
  resume: Resume.nullable(),
});
export type UserContext = z.infer<typeof UserContext>;
